import React, { useEffect, useState } from 'react';
import { notification, Spin, Input, Button, Flex, Form, Image } from 'antd';
import { ContextStatus, productTypeAsString } from '@leedpulse-monorepo/enums';
import { useSearchParams } from 'react-router-dom';
import {
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from 'firebase/auth';
import classNames from 'classnames';
import { useTheme } from '../../contexts/ui/ThemeContext';
import clearPasswordChangeAfterReset from '../../../domain/use_cases/auth/clearPasswordChangeAfterReset';

import { LeedflowLogoLight as logoLeedflow } from '@leedpulse-monorepo/assets';

import styles from './PasswordResetForm.module.css';

interface PasswordResetFormValues {
  password: string;
  confirmPassword: string;
}

const PASSWORD_MIN_LENGTH = 8;

const PasswordResetForm: React.FC = () => {
  const { themeName } = useTheme();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<PasswordResetFormValues>();

  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [isCheckingLink, setIsCheckingLink] = useState(true);
  const [email, setEmail] = useState<string | null>(null);

  const oobCode = searchParams.get('oobCode');
  const redirect = searchParams.get('redirect');

  const handleSubmit = async (values: PasswordResetFormValues) => {
    if (!oobCode || !redirect) {
      return;
    }

    setStatus(ContextStatus.LOADING);

    try {
      await confirmPasswordReset(getAuth(), oobCode, values.password);
      // A password chosen here replaces the one sent by mail: no new change at the next login
      if (email) {
        await clearPasswordChangeAfterReset(email, values.password);
      }
      notification.success({
        message: 'Mot de passe mis à jour',
        description: 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.',
      });

      setTimeout(() => {
        window.location.href = redirect;
      }, 5000);
    } catch (error) {
      console.error('Error confirming password reset:', error);
      setStatus(ContextStatus.ERROR);
      notification.error({
        message: 'Erreur',
        description: 'Impossible de réinitialiser le mot de passe. Le lien est peut-être expiré.',
      });
    }
  };

  useEffect(() => {
    const verifyResetLink = async () => {
      if (!oobCode) {
        setIsLinkValid(false);
        setIsCheckingLink(false);
        return;
      }

      try {
        setEmail(await verifyPasswordResetCode(getAuth(), oobCode));
        setIsLinkValid(true);
      } catch (error) {
        console.error('Invalid password reset link:', error);
        setIsLinkValid(false);
      } finally {
        setIsCheckingLink(false);
      }
    };

    verifyResetLink();
  }, [oobCode]);

  const isLoading = status === ContextStatus.LOADING;

  if (isCheckingLink) {
    return (
      <div className={styles.resetPassword}>
        <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
          <Spin size="large" />
        </Flex>
      </div>
    );
  }

  if (!isLinkValid) {
    return (
      <div className={styles.resetPassword}>
        <div className={styles.resetPasswordContainer}>
          <div className={styles.resetPasswordLogo}>
            <Image
              src={logoLeedflow}
              alt="Logo"
              preview={false}
              style={{
                marginTop: "1rem",
                marginBottom: "1.5rem",
                padding: "0.75rem 1.5rem",
              }}
            />
          </div>
          <p className={styles.resetPasswordSubtitle}>
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          {redirect ? <Button type="primary" onClick={() => window.location.href = redirect} style={{ width: '100%' }}>
            Retour à la connexion
          </Button> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resetPassword}>
      {/* Decorative arcs - Top Right */}
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr1)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr2)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr3)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr4)} />

      {/* Decorative arcs - Bottom Left */}
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl1)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl2)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl3)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl4)} />

      <div className={styles.resetPasswordContainer}>
        <div className={styles.resetPasswordLogo}>
          <Image
            src={logoLeedflow}
            alt="Logo"
            preview={false}
            style={{
              marginTop: "1rem",
              marginBottom: "1.5rem",
              padding: "0.75rem 1.5rem",
            }}
          />
        </div>

        <p className={styles.resetPasswordSubtitle}>Choisissez un nouveau mot de passe pour votre compte.</p>

        <Form
          className={styles.resetPasswordForm}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div className={styles.resetPasswordField}>
            <div className={styles.resetPasswordInputWrapper}>
              <Form.Item
                label="Mot de passe :"
                name="password"
                rules={[
                  { required: true, message: 'Le mot de passe est obligatoire.' },
                  { min: PASSWORD_MIN_LENGTH, message: `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères.` },
                ]}
              >
                <Input.Password className={classNames(styles.resetPasswordInput, styles.resetPasswordInputPassword)} />
              </Form.Item>
            </div>
          </div>

          <div className={styles.resetPasswordField}>
            <div className={styles.resetPasswordInputWrapper}>
              <Form.Item
                label="Confirmation du mot de passe :"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'La confirmation du mot de passe est obligatoire.' },
                  ({ getFieldValue }) => ({
                    validator: (_, value) => {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Les mots de passe ne correspondent pas.'));
                    },
                  }),
                ]}
              >
                <Input.Password className={classNames(styles.resetPasswordInput, styles.resetPasswordInputPassword)} />
              </Form.Item>
            </div>
          </div>

          <Flex vertical gap={8}>
            <Button
              type="primary"
              htmlType="submit"
              style={{ width: '100%' }}
              disabled={isLoading}
            >
              {isLoading ? <Spin size="small" /> : 'Réinitialiser le mot de passe'}
            </Button>
          </Flex>
        </Form>

        <footer className={styles.resetPasswordFooter}>
          <p className={styles.resetPasswordCopyright}>{productTypeAsString(themeName)} • Tous droits réservés</p>
          <div className={styles.resetPasswordLegalLinks}>
            <a
              href="https://www.leedflow.com/legal/mentions-legales"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.resetPasswordLegalLink}
            >
              Mentions légales
            </a>
            <a
              href="https://www.leedflow.com/legal/politique-de-confidentialite"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.resetPasswordLegalLink}
            >
              Politique de confidentialité
            </a>
            <a
              href="https://www.leedflow.com/legal/cgu"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.resetPasswordLegalLink}
            >
              Conditions générales d'utilisation
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PasswordResetForm;
